// frontend/src/pages/admin/SupportMigration.js
// /portal-admin/support-migration — R6 helps a shop (R1) or a professional (R4/R7) import their data during
// onboarding, without ever holding their session.
//
// The risk this screen is built around is picking the WRONG shop. So the flow refuses to move until the shop
// has been identified by something the owner can read out (registered email, phone, business id), the card
// shows the details a person can verify over the phone, and the admin must type the business name to confirm.

import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const DATA_TYPES = [
  { value: "appointments", label: "Bookings & appointments" },
  { value: "customers", label: "Customers" },
  { value: "services", label: "Services" },
  { value: "staff", label: "Team members" },
];

const card = {
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 18,
  background: "#fff",
};
const label = { display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 };
const input = {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: 9,
  marginBottom: 10,
};
const primary = {
  background: "#0f766e",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "10px 14px",
  fontWeight: 600,
  cursor: "pointer",
};
const secondary = {
  background: "#fff",
  color: "#0f172a",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "10px 14px",
  fontWeight: 600,
  cursor: "pointer",
};
const muted = { color: "#64748b", fontSize: 13, margin: 0 };

function Row({ name, children }) {
  return (
    <div style={{ display: "flex", gap: 12, padding: "4px 0", fontSize: 14 }}>
      <span style={{ minWidth: 150, color: "#64748b" }}>{name}</span>
      <span style={{ fontWeight: 500 }}>{children || "—"}</span>
    </div>
  );
}

function SupportMigration() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchNote, setSearchNote] = useState("");
  const [target, setTarget] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  const [reason, setReason] = useState("");
  const [dataType, setDataType] = useState("appointments");
  const [staffId, setStaffId] = useState("");
  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [wizardMapping, setWizardMapping] = useState(null);
  const [preview, setPreview] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [history, setHistory] = useState([]);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const loadHistory = useCallback(
    async (businessId) => {
      if (!businessId) return;
      try {
        const { data } = await axios.get(
          `${API_BASE_URL}/api/support-import/history?businessId=${businessId}`,
          { headers: authHeaders() },
        );
        setHistory(data.history || []);
      } catch (err) {
        setHistory([]);
      }
    },
    [authHeaders],
  );

  useEffect(() => {
    if (target) loadHistory(target.businessId);
  }, [target, loadHistory]);

  const search = async (event) => {
    event.preventDefault();
    setSearching(true);
    setSearchNote("");
    setResults([]);
    setMessage(null);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/support-import/targets?q=${encodeURIComponent(query.trim())}`,
        { headers: authHeaders() },
      );
      setResults(data.targets || []);
      if (!data.targets?.length) {
        setSearchNote(data.message || "No shop matched. Try the owner's registered email.");
      }
    } catch (err) {
      setSearchNote(err.response?.data?.message || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const choose = async (businessId) => {
    setMessage(null);
    setFile(null);
    setColumns([]);
    setPreview([]);
    setMapping({});
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/support-import/targets/${businessId}`,
        { headers: authHeaders() },
      );
      setTarget(data.target);
      setConfirmName("");
      // Default to the whole shop rather than silently picking the first professional: which calendar a
      // booking lands in must be a deliberate choice.
      setStaffId("");
    } catch (err) {
      setMessage({ type: "error", text: "Could not load that business." });
    }
  };

  const endpointFor = () => {
    if (dataType === "appointments") {
      return staffId
        ? `${API_BASE_URL}/api/staff/bookings/import`
        : `${API_BASE_URL}/api/bookings/import`;
    }
    return `${API_BASE_URL}/api/import/business/${target.businessId}/execute-import`;
  };

  const buildForm = (extra = {}) => {
    const form = new FormData();
    form.append("businessId", target.businessId);
    if (staffId) form.append("staffId", staffId);
    if (reason.trim()) form.append("reason", reason.trim());
    for (const [key, value] of Object.entries(extra)) {
      if (value !== undefined && value !== null) form.append(key, value);
    }
    if (file) form.append("file", file);
    return form;
  };

  // Mapping rules live in a plain module so they can be tested directly — see
  // tests/bookingImportMapping.spec.js.
  const {
    guessBookingMapping,
    missingBookingFields,
    BOOKING_FIELD_OPTIONS,
    describeField,
  } = require("../../utils/bookingImportMapping");

  const doPreview = async () => {
    if (!target?.businessId) {
      setMessage({ type: "error", text: "Search for and choose a business first." });
      return;
    }
    if (!file) {
      setMessage({ type: "error", text: "Choose a CSV file first." });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      if (dataType === "appointments") {
        const previewUrl = staffId
          ? `${API_BASE_URL}/api/staff/bookings/import/preview`
          : `${API_BASE_URL}/api/bookings/import/preview`;
        const { data } = await axios.post(previewUrl, buildForm(), { headers: authHeaders() });
        const headers = data.headers || [];
        setColumns(headers);
        setPreview(data.preview || []);
        setMapping(guessBookingMapping(headers));
        setMessage({
          type: "ok",
          text: "File read. Check the mapping below, then type the business name and run the import.",
        });
      } else {
        // Services, team members and customers go through the CSV wizard, which returns the mapping it
        // worked out; we hand that same mapping back when executing.
        const { data } = await axios.post(
          `${API_BASE_URL}/api/import/business/${target.businessId}/prepare-import`,
          buildForm({ dataType, sourcePlatform: "generic" }),
          { headers: authHeaders() },
        );
        if (!data.success) {
          setMessage({ type: "error", text: "That file could not be read." });
          return;
        }
        setWizardMapping(data.mapping || null);
        setColumns([]);
        setPreview(data.dataPreview?.map((row) => row.data) || []);
        const conflictCount = data.conflicts?.total || 0;
        setMessage({
          type: "ok",
          text: `File read — ${conflictCount} possible duplicate(s) found. Type the business name and run the import.`,
        });
      }
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || err.response?.data?.error || "Could not read that file.",
      });
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    if (!target?.businessId) {
      setMessage({ type: "error", text: "Search for and choose a business first." });
      return;
    }
    if (confirmName.trim() !== target.name) {
      setMessage({ type: "error", text: "Type the business name exactly to confirm." });
      return;
    }

    // Preview first if the file has not been read yet: the mapping dropdowns do not exist until then, and
    // telling someone to "map these" when there is nothing to map with is a dead end.
    if (dataType === "appointments" && columns.length === 0) {
      await doPreview();
      return;
    }

    // Pre-flight: a mapping that leaves required fields unmapped imports nothing and reports two errors.
    // Better to refuse here than to create an empty run the admin has to decode afterwards.
    if (dataType === "appointments") {
      const missing = missingBookingFields(mapping);
      if (missing.length > 0) {
        const named = missing.map(describeField).join(", ");
        setMessage({
          type: "error",
          text: `Map these before importing: ${named}. Nothing was sent — use the dropdowns under "Which column is which?".`,
        });
        return;
      }
    }

    setBusy(true);
    setMessage(null);
    try {
      let payload;
      if (dataType === "appointments") {
        const columnMapping = {};
        for (const [header, field] of Object.entries(mapping)) {
          if (field) columnMapping[field] = header;
        }
        payload = buildForm({ columnMapping: JSON.stringify(columnMapping) });
      } else {
        payload = buildForm({
          dataType,
          sourcePlatform: "generic",
          mapping: wizardMapping ? JSON.stringify(wizardMapping) : undefined,
          conflictResolutions: JSON.stringify({}),
        });
      }

      const { data } = await axios.post(endpointFor(), payload, { headers: authHeaders() });
      const imported = data.imported ?? data.summary?.imported ?? 0;
      const failed = data.failed ?? data.summary?.failed ?? 0;
      const needsAssignment = Number(data.needsAssignment) || 0;
      const firstError = (data.errors || [])[0];
      const firstFailure =
        typeof firstError === "string" ? firstError : firstError?.error || firstError?.message;

      if (imported === 0 && (failed > 0 || firstFailure)) {
        // Say why, not just how many. "0 imported · 2 errors" is not actionable.
        setMessage({
          type: "error",
          text: `Nothing was imported. ${firstFailure || "The rows did not match the shop's records."}`,
        });
      } else {
        setMessage({
          type: "ok",
          text: `Import finished — ${imported} record(s) imported into ${target.name}${
            needsAssignment
              ? `; ${needsAssignment} have no professional yet, so the shop can assign them from Bookings → Needs assignment`
              : ""
          }${failed ? `; ${failed} row(s) could not be imported` : ""}. The owner has been notified and the run is in their history.`,
        });
      }
      setFile(null);
      setColumns([]);
      setPreview([]);
      setWizardMapping(null);
      loadHistory(target.businessId);
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || err.response?.data?.error || "Import failed. Nothing was written.",
      });
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {    setTarget(null);
    setResults([]);
    setQuery("");
    setMessage(null);
    setHistory([]);
  };

  const unmappedRequired = missingBookingFields(mapping);

  return (
    <PortalAdminLayout>      <div style={{ maxWidth: 900, margin: "0 auto", padding: 24 }}>
        <h1 style={{ marginBottom: 4 }}>Support-assisted migration</h1>
        <p style={{ ...muted, marginBottom: 20 }}>
          Help a newly registered shop import their data. Nothing is imported until the shop is identified
          and its name is typed — and the owner is told what you did.
        </p>

        {message && (
          <div
            style={{
              ...card,
              marginBottom: 16,
              borderColor: message.type === "error" ? "#fecaca" : "#bbf7d0",
              background: message.type === "error" ? "#fef2f2" : "#f0fdf4",
            }}
          >
            {message.text}
          </div>
        )}

        {!target && (
          <form onSubmit={search} style={card}>
            <label style={label} htmlFor="support-search">
              Find the shop
            </label>
            <input
              id="support-search"
              style={input}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Owner's registered email, business name, phone, or business id"
            />
            <p style={{ ...muted, marginBottom: 12 }}>
              The owner's registered email is the safest identifier — it is unique and the owner can read it
              back to you exactly.
            </p>
            <button type="submit" style={primary} disabled={searching || query.trim().length < 3}>
              {searching ? "Searching…" : "Search"}
            </button>
            {searchNote && <p style={{ ...muted, marginTop: 12 }}>{searchNote}</p>}

            {results.length > 0 && (
              <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
                {results.map((item) => (
                  <div key={item.businessId} style={{ ...card, boxShadow: "0 1px 2px rgba(15,23,42,.06)" }}>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>{item.name}</div>
                    <div style={{ ...muted, marginTop: 2 }}>
                      {[item.city, item.state].filter(Boolean).join(", ") || "No city on file"} ·{" "}
                      {item.locations.length} location(s) · owner {item.owner.email || "no email"}
                    </div>
                    <div style={{ ...muted, marginTop: 2 }}>
                      Joined {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "—"} ·{" "}
                      {item.isLive ? "live" : "not live"} · id {item.businessId}
                    </div>
                    <button type="button" style={{ ...primary, marginTop: 12 }} onClick={() => choose(item.businessId)}>
                      This is the shop
                    </button>
                  </div>
                ))}
              </div>
            )}
          </form>
        )}

        {target && (
          <>
            <div style={{ ...card, marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <h2 style={{ margin: 0 }}>{target.name}</h2>
                <button type="button" style={secondary} onClick={reset}>
                  Start over
                </button>
              </div>
              <div style={{ marginTop: 12 }}>
                <Row name="Owner">{`${target.owner.name} · ${target.owner.email || "no email"}`}</Row>
                <Row name="Owner phone">{target.owner.phone}</Row>
                <Row name="Business phone">{target.phone}</Row>
                <Row name="Location">{`${[target.city, target.state].filter(Boolean).join(", ")} (${
                  target.locations.map((l) => l.name).join(", ") || "none"
                })`}</Row>
                <Row name="Joined">{target.createdAt ? new Date(target.createdAt).toLocaleString() : "—"}</Row>
                <Row name="Business id">{target.businessId}</Row>
                <Row name="Status">{`${target.isLive ? "live" : "not live"} · ${target.subscriptionStatus || "no subscription"}`}</Row>
                <Row name="Last support import">
                  {target.lastSupportImportAt
                    ? new Date(target.lastSupportImportAt).toLocaleString()
                    : "never"}
                </Row>
              </div>

              <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #e2e8f0" }}>
                <label style={label} htmlFor="confirm-name">
                  Confirm — type the business name exactly
                </label>
                <input
                  id="confirm-name"
                  style={input}
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder={target.name}
                />
                <p style={muted}>
                  Check the owner email with the shop on the phone before continuing. Everything you do next
                  is recorded against your admin account and shown to the owner.
                </p>
              </div>
            </div>

            <div style={{ ...card, marginBottom: 16 }}>
              <h3 style={{ marginTop: 0 }}>What are we importing?</h3>
              <label style={label} htmlFor="data-type">
                Data type
              </label>
              <select id="data-type" style={input} value={dataType} onChange={(e) => setDataType(e.target.value)}>
                {DATA_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              {dataType === "appointments" && target.professionals.length > 0 && (
                <>
                  <label style={label} htmlFor="professional">
                    Import into whose calendar?
                  </label>
                  <select
                    id="professional"
                    style={input}
                    value={staffId}
                    onChange={(e) => setStaffId(e.target.value)}
                  >
                    <option value="">The whole shop (owner's calendar)</option>
                    {target.professionals.map((pro) => (
                      <option key={pro.staffId} value={pro.staffId}>
                        {`${pro.firstName} ${pro.lastName}`.trim() || pro.name} — {pro.role}
                        {pro.active ? "" : " (inactive)"}
                      </option>
                    ))}
                  </select>
                </>
              )}

              <label style={label} htmlFor="reason">
                Why are you doing this?
              </label>
              <input
                id="reason"
                style={input}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. owner could not upload their Square export (phone call 31 Oct)"
              />

              <label style={label} htmlFor="csv">
                CSV file from their current system
              </label>
              <input
                id="csv"
                type="file"
                accept=".csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />

              <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                <button type="button" style={secondary} onClick={doPreview} disabled={busy || !file}>
                  {busy ? "Working…" : "Read file & preview"}
                </button>
                <button
                  type="button"
                  style={primary}
                  onClick={doImport}
                  disabled={busy || !file || confirmName.trim() !== target.name}
                >
                  {busy ? "Working…" : "Run import"}
                </button>
              </div>

              {columns.length > 0 && (
                <div style={{ marginTop: 18 }}>
                  <h4 style={{ marginBottom: 4 }}>Which column is which?</h4>
                  <p style={{ ...muted, marginBottom: 10 }}>
                    We matched these from the file's headings. Change any that are wrong — the fields marked
                    * must be mapped.
                  </p>
                  {columns.map((column) => {
                    const field = mapping[column] || "";
                    const isRequired = ["customerName", "customerEmail", "appointmentDate", "startTime", "endTime", "duration"].includes(mapping[column]);
                    return (
                      <div
                        key={column}
                        style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6 }}
                      >
                        <span style={{ minWidth: 190, fontSize: 14, fontWeight: 500 }}>{column}</span>
                        <select
                          style={{
                            ...input,
                            marginBottom: 0,
                            borderColor: isRequired ? "#86efac" : "#cbd5e1",
                          }}
                          value={field}
                          onChange={(e) =>
                            setMapping((prev) => ({ ...prev, [column]: e.target.value }))
                          }
                        >
                          <option value="">— skip this column —</option>
                          {BOOKING_FIELD_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                              {option.required ? " *" : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                  {unmappedRequired.length > 0 && (
                    <p style={{ color: "#b91c1c", fontSize: 13, marginTop: 8 }}>
                      Still needed: {unmappedRequired.map(describeField).join(", ")}
                    </p>
                  )}
                  {preview.length > 0 && (
                    <p style={{ ...muted, marginTop: 10 }}>
                      {preview.length} preview row(s) read. First row:{" "}
                      {JSON.stringify(preview[0]).slice(0, 180)}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div style={card}>
              <h3 style={{ marginTop: 0 }}>Import history for this shop</h3>
              {history.length === 0 && <p style={muted}>Nothing imported yet.</p>}
              {history.map((row) => (
                <div key={row.id} style={{ padding: "10px 0", borderBottom: "1px solid #f1f5f9", fontSize: 14 }}>
                  <div>
                    <strong>{row.dataType}</strong> · {row.imported} imported
                    {row.errors ? ` · ${row.errors} error(s)` : ""} ·{" "}
                    {row.createdAt ? new Date(row.createdAt).toLocaleString() : ""}{" "}
                    {row.bySupport ? `· by support (${row.performedByName || "R6"})` : "· by the shop"}
                  </div>
                  {row.fileName && (
                    <div style={{ ...muted, fontSize: 12 }}>file: {row.fileName}</div>
                  )}
                  {row.errorRows?.length > 0 && (
                    <ul style={{ margin: "6px 0 0 18px", padding: 0, color: "#b91c1c", fontSize: 13 }}>
                      {row.errorRows.map((entry, index) => (
                        <li key={`${row.id}-${index}`}>
                          {entry.row ? `row ${entry.row}: ` : ""}
                          {entry.error}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </PortalAdminLayout>
  );
}

export default SupportMigration;
